import { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { formatApiError, isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Save, Tags, TrendingUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const CATEGORIES = ["All", "Domestic Gas", "Commercial Gas", "Electrical", "Oil", "Plumbing", "Low Carbon & Net Zero", "Retrofit & Compliance", "ECO & Energy Efficiency"];

const catColor = {
  "Domestic Gas": "bg-blue-100 text-blue-700 border-blue-200",
  "Commercial Gas": "bg-indigo-100 text-indigo-700 border-indigo-200",
  "Electrical": "bg-amber-100 text-amber-700 border-amber-200",
  "Oil": "bg-orange-100 text-orange-700 border-orange-200",
  "Plumbing": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Low Carbon & Net Zero": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Retrofit & Compliance": "bg-violet-100 text-violet-700 border-violet-200",
  "ECO & Energy Efficiency": "bg-teal-100 text-teal-700 border-teal-200",
};

export default function PricingPage() {
  useDocumentTitle("Pricing Catalogue", "Default pricing and VAT settings per qualification.");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = isAdmin;
  const [quals, setQuals] = useState([]);
  const [edits, setEdits] = useState({}); // qualId -> { default_price, default_vat_applicable }
  const [category, setCategory] = useState("All");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    try {
      const [{ data: qData }, { data: sData }] = await Promise.all([
        api.get("/qualifications"),
        api.get("/payments/summary"),
      ]);
      setQuals(qData);
      setSummary(sData);
    } catch (err) {
      if (!isAuthError(err)) toast.error("Failed to load pricing data");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = category === "All" ? quals : quals.filter(q => q.category === category);

  const setEdit = (id, field, value) => {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const getValue = (q, field, fallback) => {
    if (edits[q.id] && field in edits[q.id]) return edits[q.id][field];
    return q[field] ?? fallback;
  };

  const save = async (q) => {
    const payload = edits[q.id];
    if (!payload || Object.keys(payload).length === 0) return;
    setSavingId(q.id);
    try {
      const body = {
        default_price: parseFloat(payload.default_price ?? q.default_price ?? 0) || 0,
        default_vat_applicable: !!(payload.default_vat_applicable ?? q.default_vat_applicable ?? false),
      };
      await api.put(`/qualifications/${q.id}`, body);
      toast.success(`${q.name} pricing saved`);
      setEdits(prev => { const c = { ...prev }; delete c[q.id]; return c; });
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading pricing...</div>;
  if (!isAdmin) return (
    <div className="border border-border rounded-md bg-white p-10 text-center" data-testid="pricing-admin-only">
      <Tags size={32} className="mx-auto text-slate-300 mb-3" />
      <h2 className="font-heading font-bold text-lg mb-1">Admin Access Only</h2>
      <p className="text-sm text-muted-foreground">Pricing management is restricted to administrators.</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="pricing-page">
      {/* Revenue summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-muted-foreground">Paid Revenue</p>
            <TrendingUp size={14} className="text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-foreground" data-testid="summary-paid">£{(summary?.paid_total || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{summary?.paid?.count || 0} payments · VAT £{(summary?.paid?.vat || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-lg border border-border p-4">
          <p className="text-xs text-muted-foreground mb-1">Outstanding</p>
          <p className="text-2xl font-bold text-amber-700" data-testid="summary-outstanding">£{(summary?.outstanding_total || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{(summary?.unpaid?.count || 0) + (summary?.invoice_sent?.count || 0)} invoices waiting</p>
        </div>
        <div className="bg-white rounded-lg border border-border p-4">
          <p className="text-xs text-muted-foreground mb-1">Overdue</p>
          <p className="text-2xl font-bold text-red-700" data-testid="summary-overdue">£{(summary?.overdue?.total || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{summary?.overdue?.count || 0} overdue</p>
        </div>
        <div className="bg-white rounded-lg border border-border p-4">
          <p className="text-xs text-muted-foreground mb-1">Refunded</p>
          <p className="text-2xl font-bold text-slate-700" data-testid="summary-refunded">£{(summary?.refunded?.total || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{summary?.refunded?.count || 0} refunds</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-border">
        <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Tags size={18} className="text-slate-600" />
            <h2 className="font-heading font-semibold text-base">Pricing Catalogue</h2>
            <Badge variant="outline" className="text-[10px]">{filtered.length} qualifications</Badge>
          </div>
          <Tabs value={category} onValueChange={setCategory}>
            <TabsList className="h-8 flex-wrap">
              {CATEGORIES.map(c => <TabsTrigger key={c} value={c} className="text-xs h-7">{c}</TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Qualification</th>
                <th className="text-left px-4 py-2.5 font-medium">Category</th>
                <th className="text-left px-4 py-2.5 font-medium">Code</th>
                <th className="text-right px-4 py-2.5 font-medium">Default Price (£)</th>
                <th className="text-center px-4 py-2.5 font-medium">VAT (20%)</th>
                <th className="text-right px-4 py-2.5 font-medium">Net + VAT</th>
                {canEdit && <th className="text-right px-4 py-2.5 font-medium w-20">Save</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(q => {
                const price = parseFloat(getValue(q, "default_price", 0)) || 0;
                const vat = !!getValue(q, "default_vat_applicable", false);
                const total = vat ? price * 1.2 : price;
                const dirty = !!edits[q.id];
                return (
                  <tr key={q.id} className={`border-b border-border last:border-0 hover:bg-slate-50 ${dirty ? "bg-amber-50" : ""}`} data-testid={`pricing-row-${q.id}`}>
                    <td className="px-4 py-3 font-medium text-foreground">{q.name}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className={`text-[10px] ${catColor[q.category] || ""}`}>{q.category}</Badge></td>
                    <td className="px-4 py-3 text-muted-foreground text-xs font-mono">{q.code || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {canEdit ? (
                        <Input
                          type="number" step="0.01" min="0"
                          value={getValue(q, "default_price", 0)}
                          onChange={(e) => setEdit(q.id, "default_price", e.target.value)}
                          className="h-8 w-24 text-right inline-block"
                          data-testid={`price-input-${q.id}`}
                        />
                      ) : <span className="font-medium">£{price.toFixed(2)}</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {canEdit ? (
                        <Switch
                          checked={vat}
                          onCheckedChange={(v) => setEdit(q.id, "default_vat_applicable", v)}
                          data-testid={`vat-toggle-${q.id}`}
                        />
                      ) : (vat ? <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">+20%</Badge> : <span className="text-xs text-muted-foreground">Exempt</span>)}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground">£{total.toFixed(2)}</td>
                    {canEdit && (
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm" variant={dirty ? "default" : "ghost"}
                          className="h-7 px-2"
                          disabled={!dirty || savingId === q.id}
                          onClick={() => save(q)}
                          data-testid={`save-pricing-${q.id}`}
                        >
                          <Save size={13} />
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div className="p-12 text-center text-muted-foreground text-sm">No qualifications in this category</div>}
      </div>
    </div>
  );
}
